import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTermometroReceptor } from '../hooks/useTermometro';
import type { TipoVoto, TermometroClique } from '../hooks/useTermometro';

// ── Constantes ────────────────────────────────────────────────────────────────

const INATIVIDADE_MS   = 15_000; // 15 s sem clique → HUD some
const EMOJI_DURACAO_MS = 2_000;  // emoji vive 2 s no DOM

const EMOJIS_APOIAR = ['🚀', '⚽', '🔥', '🏆', '💪', '👏', '🎉', '✨'];
const EMOJIS_SECAR  = ['🤬', '😤', '💢', '🪄', '❌', '😡', '👎', '🤦'];

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface EmojiParticula {
  id:       number;
  emoji:    string;
  tipo:     TipoVoto;
  left:     number; // % da largura do container
  delay:    number; // ms de delay inicial na animação
  swayDir:  'left' | 'right';
  fontSize: number;
}

interface Props {
  telaId:    string;
  fixtureId: number;
}

let particleCounter = 0;

// ── Componente principal ──────────────────────────────────────────────────────

/**
 * TvFutebolOverlay — HUD invisível sobreposto ao vídeo HDMI.
 *
 * Posicionamento:  absolute inset-0, pointer-events-none, z-50
 * Emojis:          flutuam de baixo para cima com transform/opacity (GPU only)
 * Placar HUD:      cabo de guerra translúcido no rodapé central
 * Auto-hide:       15 s de inatividade → opacity 0 com transição suave
 *
 * Performance: cada emoji é desmontado do DOM após 2 s para não acumular nós.
 */
export default function TvFutebolOverlay({ telaId, fixtureId }: Props) {
  const [particulas,    setParticulas]    = useState<EmojiParticula[]>([]);
  const [hudVisivel,    setHudVisivel]    = useState(false);

  const inativTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const particulaTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // ── Reinicia o timer de inatividade ──────────────────────────────────────
  const resetarInatividade = useCallback(() => {
    setHudVisivel(true);
    if (inativTimerRef.current) clearTimeout(inativTimerRef.current);
    inativTimerRef.current = setTimeout(() => setHudVisivel(false), INATIVIDADE_MS);
  }, []);

  useEffect(() => () => {
    if (inativTimerRef.current) clearTimeout(inativTimerRef.current);
    particulaTimersRef.current.forEach(clearTimeout);
    particulaTimersRef.current.clear();
  }, []);

  // ── Handler de clique recebido via Broadcast ──────────────────────────────
  const onClique = useCallback((clique: TermometroClique) => {
    const { tipo, time } = clique;
    resetarInatividade();

    // Cria partícula de emoji
    const pool = tipo === 'apoiar' ? EMOJIS_APOIAR : EMOJIS_SECAR;
    const emoji = pool[Math.floor(Math.random() * pool.length)];

    // Lado de origem: time casa → esquerda; visitante → direita
    const leftBase = time === 'casa'
      ? 10 + Math.random() * 30  // 10–40% da largura
      : 60 + Math.random() * 30; // 60–90% da largura

    const particula: EmojiParticula = {
      id:      ++particleCounter,
      emoji,
      tipo,
      left:    leftBase,
      delay:   Math.random() * 80,
      swayDir: Math.random() > 0.5 ? 'left' : 'right',
      fontSize: 2.5 + Math.random() * 1.5,
    };

    setParticulas(prev => [...prev, particula]);

    // Remove do DOM após a animação completar
    const timer = setTimeout(() => {
      particulaTimersRef.current.delete(timer);
      setParticulas(prev => prev.filter(p => p.id !== particula.id));
    }, EMOJI_DURACAO_MS + 100);
    particulaTimersRef.current.add(timer);
  }, [resetarInatividade]);

  // O placar vem dos totais absolutos do banco, hidratados a cada rejoin do
  // canal — nunca de um acumulador local. Ver useTermometroReceptor.
  const { apoiar: totalApoiar, secar: totalSecar } = useTermometroReceptor(telaId, fixtureId, onClique);

  const total     = totalApoiar + totalSecar;
  const pctApoiar = total > 0 ? Math.round((totalApoiar / total) * 100) : 50;
  const pctSecar  = 100 - pctApoiar;

  return (
    <>
      {/* ── Container overlay ── */}
      <div
        className="absolute inset-0 pointer-events-none z-50 overflow-hidden"
        aria-hidden="true"
      >

        {/* ── Partículas de emoji (Framer Motion) ── */}
        {particulas.map(p => (
          <motion.span
            key={p.id}
            style={{
              position:   'absolute',
              bottom:     '8%',
              left:       `${p.left}%`,
              fontSize:   `${p.fontSize}vw`,
              lineHeight: 1,
              userSelect: 'none',
            }}
            initial={{ y: 0, opacity: 0, scale: 0.6 }}
            animate={{
              y:       p.swayDir === 'left' ? [0, '-42vh', '-85vh'] : [0, '-42vh', '-85vh'],
              x:       p.swayDir === 'left' ? [0, '-3vw', '-6vw']  : [0, '3vw',  '6vw'],
              opacity: [0, 1, 1, 0],
              scale:   [0.6, 1.1, 0.8],
            }}
            transition={{
              duration: EMOJI_DURACAO_MS / 1000,
              delay:    p.delay / 1000,
              ease:     'easeOut',
            }}
          >
            {p.emoji}
          </motion.span>
        ))}

        {/* ── HUD: cabo de guerra no rodapé ── */}
        <div
          style={{
            position:   'absolute',
            bottom:     '2vw',
            left:       '50%',
            transform:  'translateX(-50%)',
            transition: 'opacity 0.8s ease',
            opacity:    hudVisivel ? 1 : 0,
            minWidth:   '28vw',
          }}
        >
          <div
            style={{
              background:    'rgba(0,0,0,0.72)',
              backdropFilter:'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border:        '1px solid rgba(255,255,255,0.12)',
              borderRadius:  '1vw',
              padding:       '0.6vw 1.2vw',
            }}
          >
            {/* Labels */}
            <div className="flex items-center justify-between mb-1.5" style={{ gap: '1vw' }}>
              <div className="flex flex-col" style={{ gap: '2px' }}>
                <div className="flex items-center gap-1" style={{ fontSize: '0.7vw', color: '#4ade80', fontWeight: 700 }}>
                  <span>🚀</span>
                  <span>APOIAR {pctApoiar}%</span>
                </div>
                <span style={{ fontSize: '0.5vw', color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em' }}>
                  CASA
                </span>
              </div>
              <span style={{ fontSize: '0.55vw', color: 'rgba(255,255,255,0.35)', fontWeight: 600, letterSpacing: '0.1em', textAlign: 'center' }}>
                TORCIDA · {total.toLocaleString('pt-BR')} votos
              </span>
              <div className="flex flex-col items-end" style={{ gap: '2px' }}>
                <div className="flex items-center gap-1" style={{ fontSize: '0.7vw', color: '#f87171', fontWeight: 700 }}>
                  <span>SECAR {pctSecar}%</span>
                  <span>🤬</span>
                </div>
                <span style={{ fontSize: '0.5vw', color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em' }}>
                  VISITANTE
                </span>
              </div>
            </div>

            {/* Barra de cabo de guerra */}
            <div
              style={{
                display:      'flex',
                height:       '0.5vw',
                borderRadius: '999px',
                overflow:     'hidden',
                minHeight:    6,
              }}
            >
              <div
                style={{
                  width:      `${pctApoiar}%`,
                  background: 'linear-gradient(90deg, #059669, #4ade80)',
                  transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
                  borderRadius:'999px 0 0 999px',
                }}
              />
              <div
                style={{
                  flex:       1,
                  background: 'linear-gradient(90deg, #f87171, #dc2626)',
                  borderRadius:'0 999px 999px 0',
                }}
              />
            </div>

            {/* Emoji indicador de liderança */}
            {total >= 5 && (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: '0.4vw',
                  fontSize:  '0.65vw',
                  color:     pctApoiar > pctSecar ? '#4ade80' : '#f87171',
                  fontWeight:700,
                }}
              >
                {pctApoiar > pctSecar + 10
                  ? '🔥 Torcida domina!'
                  : pctSecar > pctApoiar + 10
                    ? '😤 Pressão total!'
                    : '⚖️ Tá equilibrado!'}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
